import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

const FREE_AI_LIMIT = 25;


// ============================================================
// HELPERS
// ============================================================

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}


function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}


function getDisplayName(user) {
  return (
    user.full_name ||
    user.username ||
    user.email ||
    "Unnamed User"
  );
}


function getPlanInfo(user) {
  if (user.is_superuser) {
    return {
      label: "SUPERUSER",
      type: "superuser",
    };
  }

  if (user.has_valid_trial) {
    return {
      label: "TRIAL",
      type: "trial",
    };
  }

  if (user.has_valid_paid_subscription) {
    const paymentType =
      String(
        user.payment_type || ""
      ).toLowerCase();

    if (paymentType.includes("quarter")) {
      return {
        label: "QUARTERLY",
        type: "paid",
      };
    }

    if (paymentType.includes("month")) {
      return {
        label: "MONTHLY",
        type: "paid",
      };
    }

    return {
      label:
        user.payment_type
          ? String(
              user.payment_type
            ).toUpperCase()
          : "PAID",
      type: "paid",
    };
  }

  if (
    user.subscription_status ===
    "past_due"
  ) {
    return {
      label: "PAST DUE",
      type: "warning",
    };
  }

  if (
    user.subscription_status ===
    "canceled"
  ) {
    return {
      label: "CANCELED",
      type: "inactive",
    };
  }

  return {
    label: "FREE",
    type: "free",
  };
}


function getBadgeStyle(type) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "28px",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
  };

  if (type === "superuser") {
    return {
      ...base,
      backgroundColor: "#ecfdf3",
      color: "#15803d",
      border: "1px solid #bbf7d0",
    };
  }

  if (type === "trial") {
    return {
      ...base,
      backgroundColor: "#eff6ff",
      color: "#2563eb",
      border: "1px solid #bfdbfe",
    };
  }

  if (type === "paid") {
    return {
      ...base,
      backgroundColor: "#eef2ff",
      color: "#4f46e5",
      border: "1px solid #c7d2fe",
    };
  }

  if (type === "warning") {
    return {
      ...base,
      backgroundColor: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fed7aa",
    };
  }

  if (type === "inactive") {
    return {
      ...base,
      backgroundColor: "#f3f4f6",
      color: "#6b7280",
      border: "1px solid #e5e7eb",
    };
  }

  return {
    ...base,
    backgroundColor: "#f8fafc",
    color: "#475569",
    border: "1px solid #e2e8f0",
  };
}


// ============================================================
// COMPONENT
//
// IMPORTANT:
// App.js may continue passing all of the old Admin Dashboard
// props. We intentionally only use goBackToHome and styles.
// This avoids changing App.js right now.
// ============================================================

export default function AdminDashboard({
  goBackToHome,
  styles,
}) {
  const [dashboardData, setDashboardData] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [planFilter, setPlanFilter] =
    useState("all");

  const [sourceFilter, setSourceFilter] =
    useState("all");

  const [activityFilter, setActivityFilter] =
    useState("all");

  const [selectedUser, setSelectedUser] =
    useState(null);

  const [showGrantForm, setShowGrantForm] =
    useState(false);

  const [grantPlan, setGrantPlan] =
    useState("monthly");

  const [
    grantDurationMonths,
    setGrantDurationMonths,
  ] = useState(6);

  const [grantPaymentMethod, setGrantPaymentMethod] =
    useState("cash");

  const [grantAmount, setGrantAmount] =
    useState("21.99");

  const [grantingPlan, setGrantingPlan] =
    useState(false);

  const [grantError, setGrantError] =
    useState("");

  const [grantSuccess, setGrantSuccess] =
    useState("");


  // ==========================================================
  // RESPONSIVE STATE
  // ==========================================================

  const [isMobile, setIsMobile] =
    useState(
      typeof window !== "undefined"
        ? window.innerWidth <= 768
        : false
    );


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(
        window.innerWidth <= 768
      );
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);


  // ==========================================================
  // FETCH ADMIN DATA
  // ==========================================================

  const fetchDashboard = async (
    isRefresh = false
  ) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");


      const {
        data: {
          session,
        },
        error: sessionError,
      } =
        await supabase.auth.getSession();


      if (
        sessionError ||
        !session?.access_token
      ) {
        throw new Error(
          "Your login session could not be verified. Please sign in again."
        );
      }


      const response =
        await fetch(
          "/api/admin-dashboard",
          {
            method: "GET",

            headers: {
              Authorization:
                `Bearer ${session.access_token}`,
            },
          }
        );


      const result =
        await response.json();


      if (!response.ok) {
        throw new Error(
          result?.error ||
          "Unable to load Admin Dashboard."
        );
      }


      setDashboardData(result);

    } catch (fetchError) {
      console.error(
        "ADMIN DASHBOARD FETCH ERROR:",
        fetchError
      );

      setError(
        fetchError?.message ||
        "Unable to load Admin Dashboard."
      );

    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ==========================================================
  // GRANT MANUAL PLAN
  // ==========================================================

  const grantManualPlan = async () => {
    if (!selectedUser?.user_id) {
      return;
    }

    try {
      setGrantingPlan(true);
      setGrantError("");
      setGrantSuccess("");


      const {
        data: {
          session,
        },
        error: sessionError,
      } =
        await supabase.auth.getSession();


      if (
        sessionError ||
        !session?.access_token
      ) {
        throw new Error(
          "Your login session could not be verified. Please sign in again."
        );
      }


      const numericAmount =
        Number(grantAmount);


      if (
        !Number.isFinite(numericAmount) ||
        numericAmount <= 0
      ) {
        throw new Error(
          "Please enter a valid payment amount."
        );
      }


      const response =
        await fetch(
          "/api/admin-grant-plan",
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${session.access_token}`,
            },

            body: JSON.stringify({
              user_id:
                selectedUser.user_id,

              plan:
                grantPlan,

              duration_months:
                grantPlan === "monthly"
                  ? 1
                  : grantPlan === "quarterly"
                  ? 3
                  : grantDurationMonths,

              payment_method:
                grantPaymentMethod,

              amount:
                numericAmount,
            }),
          }
        );


      const result =
        await response.json();


      if (!response.ok) {
        throw new Error(
          result?.error ||
          "Unable to grant manual plan."
        );
      }


      setGrantSuccess(
        result?.message ||
        "Manual plan granted successfully."
      );


      await fetchDashboard(true);


      // Update the open user modal immediately
      // using the subscription returned by the API.

      setSelectedUser(
        (currentUser) => {
          if (!currentUser) {
            return currentUser;
          }

          return {
            ...currentUser,

            access_source:
              result.subscription
                ?.access_source,

            payment_type:
              result.subscription
                ?.payment_type,

            subscription_status:
              result.subscription
                ?.subscription_status,

            current_period_start:
              result.subscription
                ?.current_period_start,

            current_period_end:
              result.subscription
                ?.current_period_end,

            cancel_at_period_end:
              result.subscription
                ?.cancel_at_period_end,

            trial_start:
              result.subscription
                ?.trial_start,

            trial_end:
              result.subscription
                ?.trial_end,

            has_valid_trial:
              false,

            has_valid_paid_subscription:
              true,

            ai_unlimited:
              true,

            ai_credits_remaining:
              FREE_AI_LIMIT,
          };
        }
      );

    } catch (grantPlanError) {

      console.error(
        "MANUAL PLAN GRANT ERROR:",
        grantPlanError
      );


      setGrantError(
        grantPlanError?.message ||
        "Unable to grant manual plan."
      );

    } finally {
      setGrantingPlan(false);
    }
  };


  useEffect(() => {
    fetchDashboard();
  }, []);


  // ==========================================================
  // FILTER USERS
  // ==========================================================

  const filteredUsers =
    useMemo(() => {
      const users =
        dashboardData?.users || [];

      const keyword =
        search
          .trim()
          .toLowerCase();


      return users.filter(
        (user) => {
          const plan =
            getPlanInfo(user);


          const matchesSearch =
            !keyword ||
            [
              user.full_name,
              user.username,
              user.email,
            ]
              .filter(Boolean)
              .some(
                (value) =>
                  String(value)
                    .toLowerCase()
                    .includes(keyword)
              );


          let matchesPlan = true;

          if (
          planFilter !== "all"
          ) {
          matchesPlan =
              plan.label
              .toLowerCase() ===
              planFilter.toLowerCase();
          }

          let matchesSource = true;

          if (sourceFilter === "stripe") {
            matchesSource =
              user.access_source === "stripe";
          }

          if (sourceFilter === "manual") {
            matchesSource =
              user.access_source === "manual";
          }

          if (sourceFilter === "free") {
            matchesSource =
              !user.access_source;
          }


            // ======================================================
            // ACTIVITY FILTER
            // ======================================================

            let matchesActivity = true;

            if (activityFilter === "ai") {
            matchesActivity =
                user.ai_questions_used > 0;
            }

            if (activityFilter === "practice") {
            matchesActivity =
                user.questions_answered > 0;
            }

            if (activityFilter === "signup-only") {
            matchesActivity =
                user.questions_answered === 0 &&
                user.ai_questions_used === 0;
            }


            return (
            matchesSearch &&
            matchesPlan &&
            matchesSource &&
            matchesActivity
            );
        }
      );
    }, [
      dashboardData,
      search,
      planFilter,
      sourceFilter,
      activityFilter,
    ]);


  const summary =
    dashboardData?.summary || {
      registered_users: 0,
      practice_users: 0,
      ai_users: 0,
      paid_users: 0,
    };


  // ==========================================================
  // LOCAL UI STYLES
  //
  // We keep these local so App.js does not need modification.
  // ==========================================================

  const pageStyle = {
    width: "100%",
  };


  const summaryGridStyle = {
    display: "grid",
    gridTemplateColumns:
      isMobile
        ? "1fr 1fr"
        : "repeat(4, minmax(0, 1fr))",
    gap: isMobile
      ? "10px"
      : "14px",
    marginTop: "22px",
    marginBottom: "26px",
  };


  const summaryCardStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #e7ebf2",
    borderRadius: "16px",
    padding: isMobile
      ? "16px 14px"
      : "20px",
    boxShadow:
      "0 4px 14px rgba(15, 23, 42, 0.04)",
  };


  const summaryLabelStyle = {
    color: "#64748b",
    fontSize: isMobile
      ? "12px"
      : "13px",
    fontWeight: 600,
    marginBottom: "8px",
  };


  const summaryValueStyle = {
    color: "#1e293b",
    fontSize: isMobile
      ? "26px"
      : "32px",
    lineHeight: 1,
    fontWeight: 800,
  };


  const sectionCardStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #e7ebf2",
    borderRadius: "18px",
    overflow: "hidden",
  };


  const sectionHeaderStyle = {
    padding: isMobile
      ? "18px"
      : "22px 24px",
    borderBottom:
      "1px solid #edf0f5",
  };


  const filterRowStyle = {
    display: "grid",
    gridTemplateColumns:
      isMobile
        ? "1fr"
        : "minmax(240px, 1fr) 160px 160px 160px",
    gap: "10px",
    marginTop: "16px",
  };


  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "44px",
    border:
      "1px solid #dbe1ea",
    borderRadius: "10px",
    padding: "10px 12px",
    backgroundColor: "#ffffff",
    color: "#1e293b",
    fontSize: "14px",
    outline: "none",
  };


  const userCardStyle = {
    padding: "18px",
    borderBottom:
      "1px solid #edf0f5",
  };


  const smallLabelStyle = {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: 600,
  };


  const smallValueStyle = {
    color: "#1e293b",
    fontSize: "14px",
    fontWeight: 700,
    marginTop: "3px",
  };


  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div style={styles.card}>
        <div style={styles.topBar}>
          <div>
            <h2
              style={
                styles.sectionTitle
              }
            >
              Admin Dashboard
            </h2>

            <div
              style={styles.quizPath}
            >
              User activity and
              subscription analytics.
            </div>
          </div>

          <button
            style={
              styles.secondaryButton
            }
            onClick={goBackToHome}
          >
            Back Home
          </button>
        </div>

        <div
          style={{
            ...styles.emptyBox,
            marginTop: "20px",
          }}
        >
          Loading Admin Dashboard...
        </div>
      </div>
    );
  }


  // ==========================================================
  // ERROR
  // ==========================================================

  if (error) {
    return (
      <div style={styles.card}>
        <div style={styles.topBar}>
          <div>
            <h2
              style={
                styles.sectionTitle
              }
            >
              Admin Dashboard
            </h2>

            <div
              style={styles.quizPath}
            >
              User activity and
              subscription analytics.
            </div>
          </div>

          <button
            style={
              styles.secondaryButton
            }
            onClick={goBackToHome}
          >
            Back Home
          </button>
        </div>

        <div
          style={{
            ...styles.emptyBox,
            marginTop: "20px",
          }}
        >
          <div
            style={{
              fontWeight: 700,
              color: "#b91c1c",
              marginBottom: "8px",
            }}
          >
            Unable to load Admin Dashboard
          </div>

          <div
            style={{
              color: "#64748b",
              marginBottom: "14px",
            }}
          >
            {error}
          </div>

          <button
            style={
              styles.smallPrimaryButton
            }
            onClick={() =>
              fetchDashboard()
            }
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }


  // ==========================================================
  // MAIN
  // ==========================================================

  return (
    <div style={styles.card}>
      <div style={pageStyle}>

        {/* ====================================================
            HEADER
        ==================================================== */}

        <div style={styles.topBar}>
          <div>
            <h2
              style={
                styles.sectionTitle
              }
            >
              Admin Dashboard
            </h2>

            <div
              style={styles.quizPath}
            >
              Monitor users, practice
              activity, AI usage, and
              subscriptions.
            </div>
          </div>


          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <button
              style={
                styles.secondaryButton
              }
              disabled={refreshing}
              onClick={() =>
                fetchDashboard(true)
              }
            >
              {refreshing
                ? "Refreshing..."
                : "Refresh"}
            </button>

            <button
              style={
                styles.secondaryButton
              }
              onClick={goBackToHome}
            >
              Back Home
            </button>
          </div>
        </div>


        {/* ====================================================
            SUMMARY
        ==================================================== */}

        <div style={summaryGridStyle}>

          <div
            style={summaryCardStyle}
          >
            <div
              style={summaryLabelStyle}
            >
              Registered Users
            </div>

            <div
              style={summaryValueStyle}
            >
              {
                summary.registered_users
              }
            </div>
          </div>


          <div
            style={summaryCardStyle}
          >
            <div
              style={summaryLabelStyle}
            >
              Practice Users
            </div>

            <div
              style={summaryValueStyle}
            >
              {
                summary.practice_users
              }
            </div>
          </div>


          <div
            style={summaryCardStyle}
          >
            <div
              style={summaryLabelStyle}
            >
              AI Users
            </div>

            <div
              style={summaryValueStyle}
            >
              {summary.ai_users}
            </div>
          </div>


          <div
            style={summaryCardStyle}
          >
            <div
              style={summaryLabelStyle}
            >
              Paid Users
            </div>

            <div
              style={summaryValueStyle}
            >
              {summary.paid_users}
            </div>
          </div>

        </div>


        {/* ====================================================
            USERS
        ==================================================== */}

        <div style={sectionCardStyle}>

          <div
            style={sectionHeaderStyle}
          >
            <div
              style={{
                display: "flex",
                alignItems:
                  isMobile
                    ? "flex-start"
                    : "center",
                justifyContent:
                  "space-between",
                gap: "12px",
                flexDirection:
                  isMobile
                    ? "column"
                    : "row",
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    color: "#1e293b",
                    fontSize: "20px",
                  }}
                >
                  User Activity &
                  Subscriptions
                </h3>

                <div
                  style={{
                    color: "#64748b",
                    fontSize: "13px",
                    marginTop: "5px",
                  }}
                >
                  {
                    filteredUsers.length
                  }{" "}
                  of{" "}
                  {
                    dashboardData
                      ?.users
                      ?.length || 0
                  }{" "}
                  users
                </div>
              </div>
            </div>


            <div
              style={filterRowStyle}
            >
              <input
                type="text"
                placeholder="Search name, username, or email..."
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                style={inputStyle}
              />


              <select
                value={planFilter}
                onChange={(event) =>
                  setPlanFilter(
                    event.target.value
                  )
                }
                style={inputStyle}
              >
                <option value="all">
                  All Plans
                </option>

                <option value="free">
                  Free
                </option>

                <option value="trial">
                  Trial
                </option>

                <option value="monthly">
                  Monthly
                </option>

                <option value="quarterly">
                  Quarterly
                </option>

                <option value="superuser">
                  SuperUser
                </option>

                <option value="past due">
                  Past Due
                </option>

                <option value="canceled">
                  Canceled
                </option>
              </select>

              <select
                value={sourceFilter}
                onChange={(event) =>
                  setSourceFilter(
                    event.target.value
                  )
                }
                style={inputStyle}
              >
                <option value="all">
                  All Sources
                </option>

                <option value="stripe">
                  Stripe
                </option>

                <option value="manual">
                  Manual
                </option>

                <option value="free">
                  Free / No Source
                </option>
              </select>

              <select
                value={activityFilter}
                onChange={(event) =>
                    setActivityFilter(
                    event.target.value
                    )
                }
                style={inputStyle}
                >
                <option value="all">
                    All Users
                </option>

                <option value="ai">
                    AI Users
                </option>

                <option value="practice">
                    Practice Users
                </option>

                <option value="signup-only">
                    Signup Only
                </option>
                </select>
            </div>
          </div>


          {/* ==================================================
              MOBILE USER CARDS
          ================================================== */}

          {isMobile ? (
            <div>
              {filteredUsers.map(
                (user) => {
                  const plan =
                    getPlanInfo(user);

                  return (
                    <div
                      key={
                        user.user_id
                      }
                      style={
                        userCardStyle
                      }
                    >
                      <div
                        style={{
                          display:
                            "flex",
                          justifyContent:
                            "space-between",
                          alignItems:
                            "flex-start",
                          gap: "10px",
                        }}
                      >
                        <div
                          style={{
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              color:
                                "#1e293b",
                              fontSize:
                                "16px",
                              fontWeight:
                                800,
                              wordBreak:
                                "break-word",
                            }}
                          >
                            {
                              getDisplayName(
                                user
                              )
                            }
                          </div>

                          <div
                            style={{
                              color:
                                "#64748b",
                              fontSize:
                                "13px",
                              marginTop:
                                "3px",
                              wordBreak:
                                "break-word",
                            }}
                          >
                            {user.email ||
                              "No email"}
                          </div>
                        </div>


                        <span
                          style={getBadgeStyle(
                            plan.type
                          )}
                        >
                          {plan.label}
                        </span>
                      </div>


                      <div
                        style={{
                          display:
                            "grid",
                          gridTemplateColumns:
                            "1fr 1fr",
                          gap: "14px",
                          marginTop:
                            "18px",
                        }}
                      >
                        <div>
                          <div
                            style={
                              smallLabelStyle
                            }
                          >
                            Answers
                          </div>

                          <div
                            style={
                              smallValueStyle
                            }
                          >
                            {
                              user.questions_answered
                            }
                          </div>
                        </div>


                        <div>
                          <div
                            style={
                              smallLabelStyle
                            }
                          >
                            AI Used
                          </div>

                          <div
                            style={
                              smallValueStyle
                            }
                          >
                            {
                              user.ai_questions_used
                            }
                          </div>
                        </div>


                        <div>
                          <div
                            style={
                              smallLabelStyle
                            }
                          >
                            AI Remaining
                          </div>

                          <div
                            style={
                              smallValueStyle
                            }
                          >
                            {user.ai_unlimited
                              ? "Unlimited"
                              : user.ai_credits_remaining}
                          </div>
                        </div>


                        <div>
                          <div
                            style={
                              smallLabelStyle
                            }
                          >
                            Joined
                          </div>

                          <div
                            style={
                              smallValueStyle
                            }
                          >
                            {formatDate(
                              user.joined_at
                            )}
                          </div>
                        </div>
                      </div>


                      <button
                        style={{
                          ...styles.smallSecondaryButton,
                          width: "100%",
                          marginTop:
                            "16px",
                        }}
                        onClick={() =>
                          setSelectedUser(
                            user
                          )
                        }
                      >
                        View Details
                      </button>
                    </div>
                  );
                }
              )}


              {filteredUsers.length ===
                0 && (
                <div
                  style={{
                    padding: "28px",
                    textAlign:
                      "center",
                    color:
                      "#64748b",
                  }}
                >
                  No users found.
                </div>
              )}
            </div>
          ) : (

            /* ================================================
               DESKTOP TABLE
            ================================================ */

            <div
              style={{
                overflowX: "auto",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse:
                    "collapse",
                  minWidth: "950px",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={
                        styles.tableHeader
                      }
                    >
                      User
                    </th>

                    <th
                      style={
                        styles.tableHeader
                      }
                    >
                      Plan
                    </th>

                    <th
                      style={
                        styles.tableHeader
                      }
                    >
                      Answers
                    </th>

                    <th
                      style={
                        styles.tableHeader
                      }
                    >
                      AI Used
                    </th>

                    <th
                      style={
                        styles.tableHeader
                      }
                    >
                      AI Remaining
                    </th>

                    <th
                      style={
                        styles.tableHeader
                      }
                    >
                      Joined
                    </th>

                    <th
                      style={
                        styles.tableHeader
                      }
                    >
                      Activity
                    </th>

                    <th
                      style={
                        styles.tableHeader
                      }
                    >
                      Details
                    </th>
                  </tr>
                </thead>


                <tbody>
                  {filteredUsers.map(
                    (user) => {
                      const plan =
                        getPlanInfo(
                          user
                        );

                      return (
                        <tr
                          key={
                            user.user_id
                          }
                        >
                          <td
                            style={
                              styles.tableCell
                            }
                          >
                            <div
                              style={{
                                fontWeight:
                                  800,
                                color:
                                  "#1e293b",
                              }}
                            >
                              {
                                getDisplayName(
                                  user
                                )
                              }
                            </div>

                            <div
                              style={{
                                fontSize:
                                  "12px",
                                color:
                                  "#64748b",
                                marginTop:
                                  "3px",
                              }}
                            >
                              {user.email ||
                                "No email"}
                            </div>

                            {user.username && (
                              <div
                                style={{
                                  fontSize:
                                    "12px",
                                  color:
                                    "#94a3b8",
                                  marginTop:
                                    "2px",
                                }}
                              >
                                @
                                {
                                  user.username
                                }
                              </div>
                            )}
                          </td>


                          <td
                            style={
                              styles.tableCell
                            }
                          >
                            <span
                              style={getBadgeStyle(
                                plan.type
                              )}
                            >
                              {
                                plan.label
                              }
                            </span>
                          </td>


                          <td
                            style={
                              styles.tableCell
                            }
                          >
                            <strong>
                              {
                                user.questions_answered
                              }
                            </strong>
                          </td>


                          <td
                            style={
                              styles.tableCell
                            }
                          >
                            <strong>
                              {
                                user.ai_questions_used
                              }
                            </strong>
                          </td>


                          <td
                            style={
                              styles.tableCell
                            }
                          >
                            {user.ai_unlimited ? (
                              <strong>
                                Unlimited
                              </strong>
                            ) : (
                              <span>
                                <strong>
                                  {
                                    user.ai_credits_remaining
                                  }
                                </strong>

                                <span
                                  style={{
                                    color:
                                      "#94a3b8",
                                  }}
                                >
                                  {" "}
                                  /{" "}
                                  {
                                    FREE_AI_LIMIT
                                  }
                                </span>
                              </span>
                            )}
                          </td>


                          <td
                            style={
                              styles.tableCell
                            }
                          >
                            {formatDate(
                              user.joined_at
                            )}
                          </td>


                          <td
                            style={
                              styles.tableCell
                            }
                          >
                            <div
                              style={{
                                fontSize:
                                  "12px",
                                color:
                                  "#475569",
                              }}
                            >
                              Practice:{" "}
                              {formatDate(
                                user.last_practice_at
                              )}
                            </div>

                            <div
                              style={{
                                fontSize:
                                  "12px",
                                color:
                                  "#475569",
                                marginTop:
                                  "4px",
                              }}
                            >
                              AI:{" "}
                              {formatDate(
                                user.last_ai_used_at
                              )}
                            </div>
                          </td>


                          <td
                            style={
                              styles.tableCell
                            }
                          >
                            <button
                              style={
                                styles.smallSecondaryButton
                              }
                              onClick={() =>
                                setSelectedUser(
                                  user
                                )
                              }
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>


              {filteredUsers.length ===
                0 && (
                <div
                  style={{
                    padding: "30px",
                    textAlign:
                      "center",
                    color:
                      "#64748b",
                  }}
                >
                  No users found.
                </div>
              )}
            </div>
          )}
        </div>


        {/* ====================================================
            USER DETAIL MODAL
        ==================================================== */}

        {selectedUser && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor:
                "rgba(15, 23, 42, 0.50)",
              zIndex: 9999,
              display: "flex",
              alignItems:
                isMobile
                  ? "flex-end"
                  : "center",
              justifyContent:
                "center",
              padding: isMobile
                ? 0
                : "24px",
              boxSizing:
                "border-box",
            }}
            onClick={() =>
              setSelectedUser(null)
            }
          >
            <div
              style={{
                width: "100%",
                maxWidth: "620px",
                maxHeight:
                  isMobile
                    ? "88vh"
                    : "85vh",
                overflowY: "auto",
                backgroundColor:
                  "#ffffff",
                borderRadius:
                  isMobile
                    ? "22px 22px 0 0"
                    : "20px",
                padding: isMobile
                  ? "22px 18px 28px"
                  : "28px",
                boxSizing:
                  "border-box",
                boxShadow:
                  "0 24px 70px rgba(15, 23, 42, 0.22)",
              }}
              onClick={(event) =>
                event.stopPropagation()
              }
            >
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "flex-start",
                  gap: "12px",
                }}
              >
                <div>
                  <h2
                    style={{
                      margin:
                        "0 0 4px",
                      color:
                        "#1e293b",
                      fontSize:
                        "22px",
                    }}
                  >
                    {getDisplayName(
                      selectedUser
                    )}
                  </h2>

                  <div
                    style={{
                      color:
                        "#64748b",
                      fontSize:
                        "14px",
                      wordBreak:
                        "break-word",
                    }}
                  >
                    {selectedUser.email ||
                      "No email"}
                  </div>
                </div>


                <span
                  style={getBadgeStyle(
                    getPlanInfo(
                      selectedUser
                    ).type
                  )}
                >
                  {
                    getPlanInfo(
                      selectedUser
                    ).label
                  }
                </span>
              </div>


              <div
                style={{
                  height: "1px",
                  backgroundColor:
                    "#edf0f5",
                  margin:
                    "22px 0",
                }}
              />


              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    isMobile
                      ? "1fr"
                      : "1fr 1fr",
                  gap: "18px",
                }}
              >
                <DetailItem
                  label="Username"
                  value={
                    selectedUser.username ||
                    "—"
                  }
                />

                <DetailItem
                  label="Account Status"
                  value={
                    selectedUser.status ||
                    "—"
                  }
                />

                <DetailItem
                  label="Joined"
                  value={formatDateTime(
                    selectedUser.joined_at
                  )}
                />

                <DetailItem
                  label="Role"
                  value={
                    selectedUser.role ||
                    "—"
                  }
                />

                <DetailItem
                  label="Questions Answered"
                  value={
                    selectedUser.questions_answered
                  }
                />

                <DetailItem
                  label="Last Practice"
                  value={formatDateTime(
                    selectedUser.last_practice_at
                  )}
                />

                <DetailItem
                  label="AI Questions Used"
                  value={
                    selectedUser.ai_questions_used
                  }
                />

                <DetailItem
                  label="AI Credits Remaining"
                  value={
                    selectedUser.ai_unlimited
                      ? "Unlimited"
                      : `${
                          selectedUser.ai_credits_remaining
                        } / ${FREE_AI_LIMIT}`
                  }
                />

                <DetailItem
                  label="Last AI Use"
                  value={formatDateTime(
                    selectedUser.last_ai_used_at
                  )}
                />

                <DetailItem
                  label="Subscription Status"
                  value={
                    selectedUser.subscription_status ||
                    "free"
                  }
                />

                <DetailItem
                  label="Payment Plan"
                  value={
                    selectedUser.payment_type ||
                    "Free"
                  }
                />

                <DetailItem
                  label="Payment Source"
                  value={
                    selectedUser.access_source
                      ? selectedUser.access_source.toUpperCase()
                      : "—"
                  }
                />

                <DetailItem
                  label="Amount Paid"
                  value={
                    selectedUser.paid_amount != null
                      ? `$${Number(
                          selectedUser.paid_amount
                        ).toFixed(2)}`
                      : "—"
                  }
                />

                <DetailItem
                  label="Paid Date"
                  value={formatDate(
                    selectedUser.paid_date
                  )}
                />

                <DetailItem
                  label="Trial Ends"
                  value={formatDateTime(
                    selectedUser.trial_end
                  )}
                />

                <DetailItem
                  label="Current Period Ends"
                  value={formatDateTime(
                    selectedUser.current_period_end
                  )}
                />

                <DetailItem
                  label="Cancel at Period End"
                  value={
                    selectedUser.cancel_at_period_end
                      ? "Yes"
                      : "No"
                  }
                />
              </div>


              {/* ==================================================
                  MANUAL PLAN GRANT
              ================================================== */}

              {!selectedUser.is_superuser && (
                <div
                  style={{
                    marginTop: "26px",
                    paddingTop: "22px",
                    borderTop: "1px solid #edf0f5",
                  }}
                >
                  {!showGrantForm ? (
                    <button
                      style={{
                        ...styles.smallPrimaryButton,
                        width: "100%",
                        minHeight: "44px",
                      }}
                      onClick={() => {
                        setGrantError("");
                        setGrantSuccess("");

                        setGrantPlan("monthly");
                        setGrantDurationMonths(1);
                        setGrantPaymentMethod("cash");
                        setGrantAmount("21.99");

                        setShowGrantForm(true);
                      }}
                    >
                      Grant Manual Plan
                    </button>
                  ) : (
                    <div
                      style={{
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "14px",
                        padding: "18px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "17px",
                          fontWeight: 800,
                          color: "#1e293b",
                          marginBottom: "4px",
                        }}
                      >
                        Grant Manual Plan
                      </div>

                      <div
                        style={{
                          fontSize: "13px",
                          color: "#64748b",
                          marginBottom: "18px",
                          lineHeight: 1.5,
                        }}
                      >
                        Manual plans do not include a trial
                        and do not renew automatically.
                      </div>


                      {/* PLAN */}

                      <div
                        style={{
                          marginBottom: "14px",
                        }}
                      >
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#475569",
                            marginBottom: "6px",
                          }}
                        >
                          Plan
                        </label>

                        <select
                          value={
                            grantPlan === "extended"
                              ? `extended-${grantDurationMonths}`
                              : grantPlan
                          }
                          onChange={(event) => {
                            const value =
                              event.target.value;

                            setGrantError("");
                            setGrantSuccess("");

                            if (value === "monthly") {
                              setGrantPlan("monthly");
                              setGrantDurationMonths(1);
                              setGrantAmount("21.99");
                              return;
                            }

                            if (value === "quarterly") {
                              setGrantPlan("quarterly");
                              setGrantDurationMonths(3);
                              setGrantAmount("59.97");
                              return;
                            }

                            if (value === "extended-6") {
                              setGrantPlan("extended");
                              setGrantDurationMonths(6);
                              setGrantAmount("");
                              return;
                            }

                            if (value === "extended-12") {
                              setGrantPlan("extended");
                              setGrantDurationMonths(12);
                              setGrantAmount("");
                            }
                          }}
                          style={inputStyle}
                          disabled={grantingPlan}
                        >
                          <option value="monthly">
                            Monthly — 1 Month — $21.99
                          </option>

                          <option value="quarterly">
                            Quarterly — 3 Months — $59.97
                          </option>

                          <option value="extended-6">
                            Extended — 6 Months
                          </option>

                          <option value="extended-12">
                            Extended — 12 Months
                          </option>
                        </select>
                      </div>


                      {/* PAYMENT METHOD */}

                      <div
                        style={{
                          marginBottom: "14px",
                        }}
                      >
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#475569",
                            marginBottom: "6px",
                          }}
                        >
                          Payment Method
                        </label>

                        <select
                          value={grantPaymentMethod}
                          onChange={(event) =>
                            setGrantPaymentMethod(
                              event.target.value
                            )
                          }
                          style={inputStyle}
                          disabled={grantingPlan}
                        >
                          <option value="cash">
                            Cash
                          </option>

                          <option value="zelle">
                            Zelle
                          </option>

                          <option value="venmo">
                            Venmo
                          </option>

                          <option value="paypal">
                            PayPal
                          </option>

                          <option value="bank_transfer">
                            Bank Transfer
                          </option>

                          <option value="check">
                            Check
                          </option>
                        </select>
                      </div>


                      {/* COLLECTED BY */}

                      <div
                        style={{
                          marginBottom: "14px",
                        }}
                      >
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#475569",
                            marginBottom: "6px",
                          }}
                        >
                          Collected By
                        </label>

                        <div
                          style={{
                            ...inputStyle,
                            backgroundColor: "#f1f5f9",
                            color: "#475569",
                            display: "flex",
                            alignItems: "center",
                            cursor: "not-allowed",
                          }}
                        >
                          {dashboardData?.admin?.email ||
                            dashboardData?.admin?.full_name ||
                            dashboardData?.admin?.username ||
                            "Unknown Admin"}
                        </div>
                      </div>

                      {/* AMOUNT */}

                      <div
                        style={{
                          marginBottom: "18px",
                        }}
                      >
                        <label
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#475569",
                            marginBottom: "6px",
                          }}
                        >
                          Amount Collected
                        </label>

                        <div
                          style={{
                            position: "relative",
                          }}
                        >
                          <span
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform:
                                "translateY(-50%)",
                              color: "#64748b",
                              fontWeight: 700,
                            }}
                          >
                            $
                          </span>

                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={grantAmount}
                            onChange={(event) =>
                              setGrantAmount(
                                event.target.value
                              )
                            }
                            disabled={grantingPlan}
                            style={{
                              ...inputStyle,
                              paddingLeft: "28px",
                            }}
                          />
                        </div>
                      </div>


                      {/* ERROR */}

                      {grantError && (
                        <div
                          style={{
                            backgroundColor: "#fef2f2",
                            border: "1px solid #fecaca",
                            color: "#b91c1c",
                            borderRadius: "10px",
                            padding: "10px 12px",
                            fontSize: "13px",
                            marginBottom: "14px",
                          }}
                        >
                          {grantError}
                        </div>
                      )}


                      {/* SUCCESS */}

                      {grantSuccess && (
                        <div
                          style={{
                            backgroundColor: "#f0fdf4",
                            border: "1px solid #bbf7d0",
                            color: "#15803d",
                            borderRadius: "10px",
                            padding: "10px 12px",
                            fontSize: "13px",
                            marginBottom: "14px",
                          }}
                        >
                          {grantSuccess}
                        </div>
                      )}


                      {/* ACTIONS */}

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "1fr 1fr",
                          gap: "10px",
                        }}
                      >
                        <button
                          style={
                            styles.secondaryButton
                          }
                          disabled={grantingPlan}
                          onClick={() => {
                            setShowGrantForm(false);
                            setGrantError("");
                            setGrantSuccess("");
                          }}
                        >
                          Cancel
                        </button>

                        <button
                          style={
                            styles.smallPrimaryButton
                          }
                          disabled={
                            grantingPlan ||
                            Boolean(grantSuccess)
                          }
                          onClick={
                            grantManualPlan
                          }
                        >
                          {grantingPlan
                            ? "Granting..."
                            : "Confirm Grant"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}


              {/* ==================================================
                  CLOSE
              ================================================== */}

              <button
                style={{
                  ...styles.secondaryButton,
                  width: "100%",
                  marginTop: "14px",
                }}
                disabled={grantingPlan}
                onClick={() => {
                  setSelectedUser(null);
                  setShowGrantForm(false);
                  setGrantError("");
                  setGrantSuccess("");
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}


// ============================================================
// DETAIL ITEM
// ============================================================

function DetailItem({
  label,
  value,
}) {
  return (
    <div>
      <div
        style={{
          color: "#64748b",
          fontSize: "12px",
          fontWeight: 600,
          marginBottom: "4px",
        }}
      >
        {label}
      </div>

      <div
        style={{
          color: "#1e293b",
          fontSize: "14px",
          fontWeight: 700,
          wordBreak: "break-word",
        }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}